import type { TanstackQueryOptions } from "@@/shared/hooks/use-tanstack-query";
import {
    useTanstackSingleQueries,
    useTanstackSingleQuery,
    useTanstackSingleSuspenseQuery,
} from "@@/shared/hooks/use-tanstack-query";
import type {
    UseQueryResult,
    UseSuspenseQueryResult,
} from "@tanstack/react-query";
import type {
    StorageItemImage,
    StorageItemImageId,
    StorageItemImageReference,
} from "@towni/common";
import {
    isStorageItemImageId,
    isStorageItemImageReference,
} from "@towni/common";
import { useMemo } from "react";
import { storageItemMainQueryKey } from "../main-query-key";

const useStorageItemImageReferenceQuery = (
    imageRef: StorageItemImageId | StorageItemImageReference | undefined,
    options: TanstackQueryOptions<StorageItemImage> = {},
): UseQueryResult<StorageItemImage> => {
    const storageItemId = isStorageItemImageReference(imageRef)
        ? imageRef.storageItemId
        : imageRef; // quick fix

    return useTanstackSingleQuery<StorageItemImage>(
        `/storage-items/${storageItemId}`,
        [storageItemMainQueryKey, storageItemId],
        {
            ...options,
            enabled:
                options.enabled !== false &&
                isStorageItemImageId(storageItemId),
        },
    );
};

/**
 * Hook that fetches a single storage item image using the specified reference.
 * This query is suspense-enabled and will throw a promise while loading.
 *
 * @param imageRef - The identifier or reference for the storage item image.
 *                   Can be either a StorageItemImageId or a StorageItemImageReference object.
 * @param options - Optional Tanstack query configuration options.
 * @returns A UseSuspenseQueryResult containing the storage item image data.
 *
 * @throws Will throw a promise when in a suspense state (while loading)
 */
const useStorageItemImageReferenceSuspenseQuery = (
    imageRef: StorageItemImageId | StorageItemImageReference | undefined,
    options: TanstackQueryOptions<StorageItemImage> = {},
): UseSuspenseQueryResult<StorageItemImage> => {
    const storageItemId = isStorageItemImageReference(imageRef)
        ? imageRef.storageItemId
        : imageRef; // quick fix

    return useTanstackSingleSuspenseQuery<StorageItemImage>(
        `/storage-items/${storageItemId}`,
        [storageItemMainQueryKey, storageItemId],
        {
            ...options,
            disabled:
                options.enabled === false ||
                !isStorageItemImageId(storageItemId),
        },
    );
};

/**
 * Will keep this for a while instead of changing
 * all the files currently using the hook
 * @param imageRef
 * @param options
 * @returns
 */
const useStorageItemImageReference = (
    imageRef: StorageItemImageId | StorageItemImageReference | undefined,
    options: TanstackQueryOptions<StorageItemImage> = {},
): readonly [
    StorageItemImage | undefined,
    UseQueryResult<StorageItemImage>,
] => {
    const queryResult = useStorageItemImageReferenceQuery(imageRef, options);

    return useMemo(() => {
        return [queryResult.data, queryResult] as const;
    }, [queryResult]);
};

const useStorageItemImageReferenceQueries = (
    storageItemImageIds: StorageItemImageId[],
    options: TanstackQueryOptions<StorageItemImage> = {},
) => {
    const queryData = storageItemImageIds.map(id => {
        return {
            route: `/storage-items/${id}`,
            queryKey: [storageItemMainQueryKey, id],
            options: {
                ...options,
                enabled: options.enabled !== false && isStorageItemImageId(id),
            },
        };
    });
    return useTanstackSingleQueries<StorageItemImage>(queryData);
};

export {
    useStorageItemImageReference,
    useStorageItemImageReferenceQueries,
    useStorageItemImageReferenceQuery,
    // Suspended (recommended)
    useStorageItemImageReferenceSuspenseQuery,
};
